/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useCallback, useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {toast} from 'utils/toastUtils'
import clsx from 'clsx'
import {useAppDispatch} from 'helpers/store'
import {useTypedSelector} from 'helpers/reducers'
import BookingAction from 'store/booking/BookingAction'
import Dates from 'utils/Dates'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import UiFormField from 'components/Forms/UiFormField'

import {Controller, useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {OfferBasicInputs, useOfferBasicSchema} from 'components/Offers/schemas/useOfferBasicSchema'
import UiOfferTimeslotsGrid from 'components/Offers/forms/UiOfferTimeslotsGrid'
import {Timeslot} from 'types/entities/Timeslot'
import UiOfferReservationsForDate from 'components/Offers/forms/UiOfferReservationsForDate'
import useSellerTimeslots from 'utils/hooks/useSellerTimeslots'
import CompanyAutocomplete from 'components/CompanyAutocomplete'
import {Company} from 'types/entities/Company'
import UiDatePickerInline from 'components/Ui/UiDatePickerInline'
import UiFormLabel from 'components/Ui/UiFormLabel'
import UiCounter from 'components/Ui/UiCounter'
import {OfferBasicFormValidationError} from 'components/Offers/enums/OfferBasicFormValidationError'
import {
	buildOfferBasicFormInitialValues,
	calculateOfferValidUntil,
	getFirstFormErrorMessage,
	getSelectedTimeslot,
	getTimeslotClock,
	hasAtLeastOnePerson,
	toCounterValue,
} from 'components/Offers/forms/OfferBasicFormUtils'
import {useOfferBasicFormSync} from 'components/Offers/hooks/useOfferBasicFormSync'
import {useOfferFlow} from "components/Offers/hooks/useOfferFlow";

type OfferBasicFormProps = {
    onChange: (values: any, shouldClose?: boolean) => void
    onBack?: () => void
    preferredTime?: string
    companyIdFromContext?: number | null
    defaultValues?: Partial<OfferBasicInputs>
    submitLabel?: string
    selectedCompanyDefault?: Company | null
    disableCompanyChange?: boolean
    showSaveAndClose?: boolean
}

const OfferBasicForm: React.FC<OfferBasicFormProps> = ({
    onChange,
    onBack,
    preferredTime,
    companyIdFromContext,
    defaultValues,
    submitLabel,
    selectedCompanyDefault,
    disableCompanyChange,
    showSaveAndClose = false,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const filter = useTypedSelector((state) => state.bookingState.bookingProcess.filter)

    const sharedLabelScope = 'OfferBasicForm text-gray-900 dark:text-gray-100'

    const selectTimeslotHandler = () => {
        // No alert - just pass through
    }

    const finishHandler = () => {}

    const { setSelectedTimeslotHandler, timeslotsAvailable, reservationsForDate, isLoadingTimeslots, selectedTimeslot } =
        useSellerTimeslots(selectTimeslotHandler, null, [null, null, null], finishHandler, null)

    const OfferBasicSchema = useOfferBasicSchema()
    const {
        register,
        setValue,
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm<OfferBasicInputs>({
        resolver: zodResolver(OfferBasicSchema),
        defaultValues: buildOfferBasicFormInitialValues({
            filter,
            defaultValues,
            companyIdFromContext,
        }),
    })

    const {
        selectedCompany,
        setSelectedCompany,
        selectedTimeslotLocal,
        setSelectedTimeslotLocal,
        setDataToFilter,
    } = useOfferBasicFormSync({
        filter,
        dispatch,
        defaultValues,
        companyIdFromContext,
        selectedCompanyDefault,
        setValue,
        reset,
    })

	const { isNextDisabled } = useOfferFlow();
	
    const autoSelectedTimeslotKeyRef = useRef<string | null>(null)

    const clearTimeslotSelection = useCallback(() => {
        setSelectedTimeslotHandler(null, false)
        setSelectedTimeslotLocal(null)
        dispatch(BookingAction.setPackageItemsSelectedTimeslot(null))
    }, [dispatch, setSelectedTimeslotHandler, setSelectedTimeslotLocal])

    const handleDateChange = useCallback((nextDate: Date | null, onFormChange: (value: string) => void) => {
        if (!nextDate || Number.isNaN(nextDate.getTime())) {
            onFormChange('')
            setValue('valid_until', '')
            clearTimeslotSelection()
            setDataToFilter({ date: '' })
            return
        }

        const nextDateIso = Dates.ymd(nextDate)
        const nextValidUntil = calculateOfferValidUntil(nextDate)

        onFormChange(nextDateIso)
        setValue('valid_until', nextValidUntil)
        clearTimeslotSelection()
        dispatch(BookingAction.setTimeslots([]))
        setDataToFilter({
            date: nextDateIso,
            timeslot_id: null,
        })
    }, [clearTimeslotSelection, dispatch, setDataToFilter, setValue])

    const handleTimeslotSelect = useCallback((timeslot: Timeslot) => {
        setSelectedTimeslotLocal(timeslot)
        setSelectedTimeslotHandler(timeslot, false)
        const { hour, minute } = getTimeslotClock(timeslot)

        dispatch(BookingAction.setPackageItemsSelectedTimeslot(timeslot))
        setDataToFilter({
            timeslot_id: timeslot.id,
            hour,
            minute,
        })
    }, [dispatch, setDataToFilter, setSelectedTimeslotHandler, setSelectedTimeslotLocal])

    useEffect(() => {
        const normalizedPreferredTime = preferredTime?.trim()
        const date = filter.date

        if (!normalizedPreferredTime || !date || timeslotsAvailable.length === 0) {
            return
        }

        const [hoursRaw, minutesRaw] = normalizedPreferredTime.split(':')
        const preferredHour = Number(hoursRaw)
        const preferredMinute = Number(minutesRaw ?? '0')

        if (Number.isNaN(preferredHour) || Number.isNaN(preferredMinute)) {
            return
        }

        const alreadySelected = selectedTimeslot || selectedTimeslotLocal
        const alreadySelectedMatches =
            !!alreadySelected &&
            new Date(alreadySelected.start_at).getHours() === preferredHour &&
            new Date(alreadySelected.start_at).getMinutes() === preferredMinute

        if (alreadySelectedMatches) {
            autoSelectedTimeslotKeyRef.current = `${date}|${preferredHour}:${preferredMinute}`
            return
        }

        const selectionKey = `${date}|${preferredHour}:${preferredMinute}`
        if (autoSelectedTimeslotKeyRef.current === selectionKey) {
            return
        }

        const matchedTimeslot = timeslotsAvailable.find((timeslot) => {
            const start = new Date(timeslot.start_at)
            const timeslotDate = Dates.ymd(start)

            // Ignore stale timeslots that belong to a previously selected date.
            if (timeslotDate !== date) {
                return false
            }

            return start.getHours() === preferredHour && start.getMinutes() === preferredMinute
        })

        if (!matchedTimeslot) {
            return
        }

        autoSelectedTimeslotKeyRef.current = selectionKey
        handleTimeslotSelect(matchedTimeslot)
    }, [
        preferredTime,
        filter.date,
        timeslotsAvailable,
        selectedTimeslot,
        selectedTimeslotLocal,
        handleTimeslotSelect,
    ])

    const handleSubmitWrapper = useCallback((values: OfferBasicInputs, shouldClose: boolean = false) => {
        const finalSelectedTimeslot = getSelectedTimeslot(selectedTimeslot, selectedTimeslotLocal)

        if (!finalSelectedTimeslot) {
            toast.error(t(OfferBasicFormValidationError.SelectTimeslot) as string)
            return
        }

        if (!hasAtLeastOnePerson(values)) {
            toast.error(t(OfferBasicFormValidationError.SelectAtLeastOnePerson) as string)
            return
        }

        onChange(values, shouldClose)
    }, [selectedTimeslot, selectedTimeslotLocal, onChange, t])

    const runSubmit = useCallback((shouldClose: boolean) => {
        handleSubmit((data) => {
            handleSubmitWrapper(data, shouldClose)
        }, (formErrors) => {
            const errorMessage = getFirstFormErrorMessage(formErrors)
            if (errorMessage) {
                toast.error(errorMessage)
            }
        })()
    }, [handleSubmit, handleSubmitWrapper])

    return (
        <UiFormInner className={clsx('OfferBasicForm flex flex-col min-h-0', sharedLabelScope)}>
            <div className="flex flex-col lg:flex-row gap-8 grow px-1">
                {/* Left Side: Controls & Reservations */}
                <div className="flex flex-col w-full lg:w-95 shrink-0 gap-3">
                    <input type="hidden" {...register('contact_id')} />
                    {/* Date Picker Section */}
                    <div className="flex flex-col gap-1 shrink-0">
                        <Controller
                            control={control}
                            name="date"
                            render={({ field: { value, onChange: onFormChange } }) => (
                                <UiDatePickerInline
                                    value={value}
                                    onChange={(nextDate) => handleDateChange(nextDate, onFormChange)}
                                    minDate={new Date()}
                                />
                            )}
                        />
                        {errors.date?.message && (
                            <div className="text-xs font-medium text-red-500 px-1">{errors.date.message}</div>
                        )}
                    </div>

                    {/* Company Section - Focused Highlight */}
                    <div className="flex flex-col gap-1 shrink-0">
                        <div className="flex items-center justify-between px-0.5">
                            <UiFormLabel className="mb-0! font-bold text-gray-700 dark:text-gray-300">
                                {t('Company')}
                            </UiFormLabel>
                            {!selectedCompany && (
                                <span className="text-[10px] font-black text-red-500 dark:text-red-400 uppercase tracking-wider animate-pulse">
                                    {t('NO COMPANY SELECTED')}
                                </span>
                            )}
                        </div>
                        <div
                            className={clsx(
                                'rounded-xl transition-all',
                                !selectedCompany &&
                                    'focus-within:ring-2 focus-within:ring-amber-400 focus-within:ring-offset-2 dark:focus-within:ring-offset-gray-950'
                            )}
                        >
                            <CompanyAutocomplete
                                className="w-full"
                                name="company_id"
                                placeholder={t('Search for company...') as string}
                                onSelect={(company: Company | null) => {
                                    setSelectedCompany(company)
                                    setValue('company_id', company ? Number(company.id) : undefined, {
                                        shouldValidate: true,
                                    })
                                }}
                                defaultValue={selectedCompany}
                                label=""
                                disabled={disableCompanyChange}
                                allowCreate={true}
                                autoCreate={true}
                            />
                        </div>
                        {errors.company_id?.message && (
                            <div className="text-xs font-medium text-red-500 px-0.5">{errors.company_id.message}</div>
                        )}

                        <Controller
                            control={control}
                            name="valid_until"
                            render={({ field }) => (
                                <input type="hidden" value={field.value ?? ''} onChange={field.onChange} />
                            )}
                        />
                    </div>

                    {/* Compact Row (Adults, Children, Probability) */}
                    <div className="flex items-center gap-2 p-2 rounded-xl bg-gray-50 dark:bg-gray-800/20 border border-gray-100 dark:border-gray-800 shrink-0">
                        <div className="flex-1 min-w-0">
                            <UiFormField label={t('Adults')} error={errors.adults?.message} className="mb-0!">
                                <Controller
                                    control={control}
                                    name="adults"
                                    render={({ field }) => (
                                        <UiCounter
                                            value={field.value}
                                            onChange={(val) => {
                                                const finalVal = toCounterValue(val)
                                                field.onChange(finalVal)
                                                setDataToFilter({
                                                    adults: finalVal,
                                                })
                                            }}
                                            min={0}
                                            className="h-8 shadow-none"
                                            inputClassName="text-xs!"
                                        />
                                    )}
                                />
                            </UiFormField>
                        </div>
                        <div className="flex-1 min-w-0">
                            <UiFormField label={t('Children')} error={errors.children?.message} className="mb-0!">
                                <Controller
                                    control={control}
                                    name="children"
                                    render={({ field }) => (
                                        <UiCounter
                                            value={field.value}
                                            onChange={(val) => {
                                                const finalVal = toCounterValue(val)
                                                field.onChange(finalVal)
                                                setDataToFilter({
                                                    children: finalVal,
                                                })
                                            }}
                                            min={0}
                                            className="h-8 shadow-none"
                                            inputClassName="text-xs!"
                                        />
                                    )}
                                />
                            </UiFormField>
                        </div>
                        <div className="flex-1 min-w-0">
                            <UiFormField
                                label={t('Probability %')}
                                error={errors.probability?.message}
                                className="mb-0!"
                            >
                                <Controller
                                    control={control}
                                    name="probability"
                                    render={({ field }) => (
                                        <UiCounter
                                            value={field.value ?? ''}
                                            onChange={(val) => {
                                                const finalVal = toCounterValue(val)
                                                field.onChange(finalVal)
                                            }}
                                            min={0}
                                            max={100}
                                            step={5}
                                            className="h-8 shadow-none"
                                            inputClassName="text-xs!"
                                        />
                                    )}
                                />
                            </UiFormField>
                        </div>
                    </div>
                </div>

                {/* Right Side: Timeslots Grid (Scrollable) */}
                <div className="flex-1 min-h-0 flex flex-col bg-gray-50/50 dark:bg-gray-800/10 rounded-2xl border border-gray-100 dark:border-gray-800">
                    <div className="flex-1 overflow-y-auto p-4">
                        <UiOfferTimeslotsGrid
                            isLoading={isLoadingTimeslots}
                            timeslots={timeslotsAvailable}
                            selectedTimeslot={selectedTimeslot || selectedTimeslotLocal}
                            onSelect={handleTimeslotSelect}
                        />
                    </div>
                </div>
            </div>

            {/* Reservations Table - Separate full-width row at bottom */}
            <div className="mt-4 min-h-55">
                <UiOfferReservationsForDate reservations={reservationsForDate} className="w-full h-full" />
            </div>

            <UiFormActionsBar
                primaryText={submitLabel ?? (t('Continue') as string)}
                floatRight={true}
                disabled={(!selectedTimeslot && !selectedTimeslotLocal) || isNextDisabled}
                onPrimary={() => runSubmit(false)}
                saveAndCloseText={showSaveAndClose ? t('Save & Close') : undefined}
                onSaveAndClose={showSaveAndClose ? () => runSubmit(true) : undefined}
                backText={t('Back')}
                onBack={onBack}
                className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700"
            />
        </UiFormInner>
    )
}

export default OfferBasicForm
